import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { listEmailSends, listTemplates } from "@/lib/api";
import { fmtRelative, emailStatusLabel, emailStatusClass } from "@/lib/formatters";

const STATUS_TABS = [
  { value: "", label: "Alle" },
  { value: "delivered", label: "Afgeleverd" },
  { value: "opened", label: "Geopend" },
  { value: "clicked", label: "Geklikt" },
  { value: "bounced", label: "Bounces" },
  { value: "failed", label: "Mislukt" },
  { value: "retried", label: "Opnieuw" },
  { value: "complained", label: "Klachten" },
];

export function EmailsIndex() {
  const [status, setStatus] = useState("");
  const [templateSlug, setTemplateSlug] = useState("");
  const [q, setQ] = useState("");

  const { data: templates } = useQuery({ queryKey: ["templates"], queryFn: listTemplates });

  const { data: sends, isLoading } = useQuery({
    queryKey: ["email-sends", status, templateSlug, q],
    queryFn: () => listEmailSends({
      status: status || undefined,
      templateSlug: templateSlug || undefined,
      q: q || undefined,
      limit: 300,
    }),
  });

  const counts = useMemo(() => {
    const c: Record<string, number> = { "": sends?.length ?? 0 };
    for (const s of sends ?? []) c[s.status] = (c[s.status] ?? 0) + 1;
    return c;
  }, [sends]);

  return (
    <>
      <div className="topbar-actions" style={{ marginBottom: "1rem" }}>
        <input
          className="search"
          placeholder="🔍 Zoek e-mailadres of onderwerp…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <select className="search" value={templateSlug} onChange={(e) => setTemplateSlug(e.target.value)} style={{ width: "auto" }}>
          <option value="">Alle templates</option>
          {(templates ?? []).map((t) => <option key={t.id} value={t.slug}>{t.name}</option>)}
        </select>
      </div>

      <div className="tabs">
        {STATUS_TABS.map((t) => (
          <button
            key={t.value}
            className={`tab ${status === t.value ? "active" : ""}`}
            onClick={() => setStatus(t.value)}
          >
            {t.label} {counts[t.value] != null && <span className="badge">{counts[t.value]}</span>}
          </button>
        ))}
      </div>

      <div className="panel">
        <div className="tbl-wrap email-sends-table">
          <table className="tbl">
            <thead>
              <tr>
                <th className="hide-mobile">Verzonden</th>
                <th>Ontvanger</th>
                <th>Onderwerp</th>
                <th className="hide-mobile">Template</th>
                <th>Status</th>
                <th className="hide-mobile">Opens · Clicks</th>
                <th className="hide-mobile">Order</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={8} className="empty"><span className="spinner" /> Laden…</td></tr>}
              {(sends ?? []).map((s) => (
                <tr key={s.id}>
                  <td className="hide-mobile">{fmtRelative(s.created_at)}</td>
                  <td className="email-recipient-cell">
                    <span className="email-recipient">{s.to_email}</span>
                    <small className="show-mobile" style={{ opacity: 0.6 }}>{fmtRelative(s.created_at)}</small>
                  </td>
                  <td className="email-subject-cell">
                    {s.subject ?? <span style={{ opacity: 0.5 }}>—</span>}
                    {s.error && <small className="email-error-text">{s.error}</small>}
                  </td>
                  <td className="hide-mobile">
                    {s.template_slug
                      ? <code style={{ color: "var(--c-orange)", fontFamily: "var(--f-mono)" }}>{s.template_slug}</code>
                      : <span style={{ opacity: 0.5 }}>—</span>}
                    {s.campaign && <><br /><small style={{ opacity: 0.7 }}>{s.campaign.name}</small></>}
                  </td>
                  <td className="email-status-cell"><span className={`pill-st ${emailStatusClass(s.status)}`}>{emailStatusLabel(s.status)}</span></td>
                  <td className="hide-mobile">
                    <small>
                      {s.open_count > 0 && <>👁 {s.open_count} </>}
                      {s.click_count > 0 && <>🖱 {s.click_count}</>}
                      {s.open_count === 0 && s.click_count === 0 && <span style={{ opacity: 0.4 }}>—</span>}
                    </small>
                  </td>
                  <td className="hide-mobile">
                    {s.order
                      ? <Link to={`/orders/${s.order_id}`}><code style={{ fontFamily: "var(--f-mono)" }}>{s.order.order_no}</code></Link>
                      : <span style={{ opacity: 0.4 }}>—</span>}
                  </td>
                  <td className="email-actions-cell">
                    <div className="row-actions">
                      <Link to={`/emails/${s.id}`} className="btn-mini">Open</Link>
                    </div>
                  </td>
                </tr>
              ))}
              {!isLoading && (sends ?? []).length === 0 && (
                <tr><td colSpan={8} className="empty">Geen mails voor deze filter.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
